import { useEffect, useMemo, useState } from "react";
import { api, API, idr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Plus, Trash2, Download, Upload, Clock, Calendar, MapPin,
  Building2, Save, Loader2, PartyPopper, ListChecks, Settings, Users, Image as ImageIcon, X, Check,
  Sparkles, PlugZap, AlertTriangle, CheckCircle2, RefreshCw, MessageCircle, Send, Bell,
} from "lucide-react";
import InputRupiah from "../components/InputRupiah";
import { useCabangOptions } from "../lib/cabang";
import { DatePicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { hariIniISO } from "@/lib/tanggal";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const TABS = [
  { k: "grades",   l: "Grade Gaji",         Icon: ListChecks },
  { k: "shifts",   l: "Shift Kerja",        Icon: Clock },
  { k: "roster",   l: "Roster (Jadwal)",    Icon: Calendar },
  { k: "holidays", l: "Hari Libur",         Icon: PartyPopper },
  { k: "geofence", l: "Lokasi Kantor",      Icon: MapPin },
  { k: "cabang",   l: "Cabang",             Icon: Building2 },
  { k: "config",   l: "Konfigurasi Payroll",Icon: Settings },
  { k: "branding", l: "Logo & Nama",         Icon: ImageIcon },
  { k: "ai",       l: "Pengaturan AI",       Icon: Sparkles },
  { k: "wa",       l: "WhatsApp",            Icon: MessageCircle },
];

export default function Master() {
  const { user } = useAuth();
  const isSuper = user?.role === "superadmin";
  const isAdmin = user?.role === "admin" || isSuper;
  const [tab, setTab] = useState("grades");

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Master Data</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Konfigurasi Sistem</h1>
        <p className="mt-2 text-sm text-slate-500">Grade gaji, shift kerja, jadwal roster, hari libur, geofence kantor & parameter payroll.</p>
      </header>

      <div className="flex gap-1 mb-6 border-b border-slate-200 overflow-x-auto">
        {TABS.map(({ k, l, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-3 md:px-4 h-10 border-b-2 -mb-px text-xs md:text-sm font-medium transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
              tab === k ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}>
            <Icon className="w-3.5 h-3.5" /> {l}
          </button>
        ))}
      </div>

      {tab === "grades"   && <GradesTab isSuper={isSuper} isAdmin={isAdmin} />}
      {tab === "shifts"   && <ShiftsTab isAdmin={isAdmin} />}
      {tab === "roster"   && <RosterTab isAdmin={isAdmin} />}
      {tab === "holidays" && <HolidaysTab isAdmin={isAdmin} />}
      {tab === "geofence" && <GeofenceTab isAdmin={isAdmin} />}
      {tab === "cabang"   && <MasterCabangTab isAdmin={isAdmin} />}
      {tab === "config"   && <ConfigTab isSuper={isSuper} />}
      {tab === "branding" && <BrandingTab isAdmin={isAdmin} />}
      {tab === "ai"       && <PengaturanAITab isAdmin={isAdmin} />}
      {tab === "wa"       && <WhatsAppTab isAdmin={isAdmin} />}
    </div>
  );
}

/* ================= Master Cabang =================
 * UI untuk backend yang sudah lama siap tapi tak pernah punya layar
 * (branches_master.py): satu simpan = cabang terdaftar di HRIS (db.branches,
 * dipakai dropdown gudang/waiting list/pre-order) + akun "Kas Toko {nama}"
 * dibuat/di-reaktivasi otomatis di COA (kode 101X). Nonaktifkan = hapus dari
 * HRIS + soft-delete kas COA; cabang default dilindungi backend. */
function MasterCabangTab({ isAdmin }) {
  const { branding } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", kas_code: "", latitude: "", longitude: "", radius_meter: 100 });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/finance/branches/master")
      .then(r => setItems(r.data.items || []))
      .catch(() => toast.error("Gagal memuat daftar cabang"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const simpan = async () => {
    if (!form.name.trim()) { toast.error("Nama cabang wajib diisi"); return; }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        kas_code: form.kas_code.trim() || null,
        latitude: form.latitude === "" ? null : Number(form.latitude),
        longitude: form.longitude === "" ? null : Number(form.longitude),
        radius_meter: Number(form.radius_meter) || 100,
      };
      const { data } = await api.post("/finance/branches", body);
      toast.success(data.message || "Cabang ditambahkan ✓");
      setShowForm(false);
      setForm({ name: "", kas_code: "", latitude: "", longitude: "", radius_meter: 100 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah cabang"); }
    finally { setBusy(false); }
  };

  const nonaktif = async (name) => {
    if (!window.confirm(`Nonaktifkan cabang "${name}"? Akun kas COA-nya ikut dinonaktifkan (bisa diaktifkan lagi dengan menambah ulang nama yang sama).`)) return;
    try {
      const { data } = await api.delete(`/finance/branches/${encodeURIComponent(name)}`);
      toast.success(data.message || "Cabang dinonaktifkan ✓");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menonaktifkan"); }
  };

  // Cabang lama (bawaan Finance) punya akun kas tapi belum pernah terdaftar di
  // HRIS (db.branches) — POST endpoint yang sama meng-upsert HRIS dan memakai
  // ulang kas yang ada, jadi "koneksikan" = kirim ulang namanya saja. Geofence
  // dibiarkan kosong (di-set nanti di tab Lokasi Kantor).
  const koneksikan = async (name) => {
    try {
      const { data } = await api.post("/finance/branches", { name });
      toast.success(data.message || `Cabang '${name}' terkoneksi ✓`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengoneksikan"); }
  };
  const belumTerkoneksi = items.filter(b => !b.in_hr);
  const koneksikanSemua = async () => {
    if (!window.confirm(`Koneksikan ${belumTerkoneksi.length} cabang lama ke HRIS? Akun kas yang sudah ada dipakai ulang, tidak ada yang dobel.`)) return;
    let ok = 0;
    for (const b of belumTerkoneksi) {
      try { await api.post("/finance/branches", { name: b.name }); ok += 1; }
      catch (e) { toast.error(`${b.name}: ${e?.response?.data?.detail || "gagal"}`); }
    }
    toast.success(`${ok}/${belumTerkoneksi.length} cabang terkoneksi ✓`);
    load();
  };

  return (
    <div className="space-y-4" data-testid="cabang-tab">
      <div className="flex items-start justify-between gap-3 flex-col md:flex-row">
        <p className="text-sm text-slate-600 max-w-2xl">
          Satu kali simpan: cabang langsung terdaftar di HRIS (dipakai dropdown gudang, waiting list,
          pre-order, karyawan) <b>dan</b> akun <b>Kas Toko</b>-nya dibuat otomatis di Finance COA.
          Titik lokasi opsional — dipakai geofence absensi.
        </p>
        {isAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            {belumTerkoneksi.length > 0 && (
              <button onClick={koneksikanSemua} data-testid="cab-koneksi-semua"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-medium">
                <RefreshCw className="w-4 h-4" /> Koneksikan Semua ({belumTerkoneksi.length})
              </button>
            )}
            <button onClick={() => setShowForm(true)} data-testid="cab-tambah"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
              <Plus className="w-4 h-4" /> Tambah Cabang
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left">Cabang</th>
                <th className="px-4 py-2.5 text-center">HRIS</th>
                <th className="px-4 py-2.5 text-center">Kas COA</th>
                <th className="px-4 py-2.5 text-left">Geofence Absensi</th>
                <th className="px-4 py-2.5 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <LoadingState
                loading={loading}
                kosong={!loading && items.length === 0}
                pesanKosong="Belum ada cabang"
                varian="tabel"
                kolom={5}
                data-testid="cabang-loading"
              />
              {!loading && items.map(b => (
                <tr key={b.name} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-medium text-slate-900">{b.name}</td>
                  <td className="px-4 py-3 text-center">
                    {b.in_hr
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-emerald-50 text-emerald-700"><Check className="w-3 h-3" /> terdaftar</span>
                      : <span className="text-[11px] text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {b.in_finance_kas || b.kas_code
                      ? <span className="font-mono text-xs text-slate-700">{b.kas_code || "✓"}</span>
                      : <span className="text-[11px] text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {b.latitude != null && b.longitude != null
                      ? `${b.latitude}, ${b.longitude} · r ${b.radius_meter || 100} m`
                      : <span className="text-slate-400">belum di-set</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && (
                      <div className="inline-flex items-center gap-3">
                        {!b.in_hr && (
                          <button onClick={() => koneksikan(b.name)} data-testid={`cab-koneksi-${b.name}`}
                            className="text-xs font-medium text-emerald-700 hover:underline">Koneksikan</button>
                        )}
                        <button onClick={() => nonaktif(b.name)} data-testid={`cab-hapus-${b.name}`}
                          className="text-xs font-medium text-slate-500 hover:text-rose-700 hover:underline">Nonaktifkan</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-600" /> Tambah Cabang
            </h3>
            <p className="text-xs text-slate-500 mb-4">Kas COA dibuat otomatis (kode 10xx berikutnya) kecuali kamu isi kode sendiri.</p>
            <div className="space-y-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Nama Cabang *</span>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="cab-nama"
                  placeholder={`mis. ${branding?.nama_toko_sendiri || "Toko"} Cab. Panam`}
                  className="h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/40" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-slate-600">Kode Kas COA (opsional)</span>
                <input value={form.kas_code} onChange={e => setForm({ ...form, kas_code: e.target.value })}
                  placeholder="kosongkan = otomatis"
                  className="h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-600/40" />
              </label>
              <div className="grid grid-cols-3 gap-2">
                <label className="flex flex-col gap-1 col-span-1">
                  <span className="text-xs font-medium text-slate-600">Latitude</span>
                  <input value={form.latitude} onChange={e => setForm({ ...form, latitude: e.target.value })}
                    placeholder="opsional" className="h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none" />
                </label>
                <label className="flex flex-col gap-1 col-span-1">
                  <span className="text-xs font-medium text-slate-600">Longitude</span>
                  <input value={form.longitude} onChange={e => setForm({ ...form, longitude: e.target.value })}
                    placeholder="opsional" className="h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none" />
                </label>
                <label className="flex flex-col gap-1 col-span-1">
                  <span className="text-xs font-medium text-slate-600">Radius (m)</span>
                  <input type="number" value={form.radius_meter} onChange={e => setForm({ ...form, radius_meter: e.target.value })}
                    className="h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none" />
                </label>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
              <button onClick={simpan} disabled={busy || !form.name.trim()} data-testid="cab-simpan"
                className="flex-1 h-10 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">
                {busy ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ================= WhatsApp (Fonnte) ================= */
function WhatsAppTab({ isAdmin }) {
  const [st, setSt] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  // Form pengaturan dipisah dari `st` supaya ketikan admin tidak tertimpa
  // saat "Muat Ulang" ditekan di tengah mengedit.
  const [form, setForm] = useState({ enabled: true, nomor_pengirim: "" });
  const [saving, setSaving] = useState(false);
  const [noHp, setNoHp] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const loadStatus = () => {
    setLoadErr("");
    api.get("/wa/status")
      .then(r => {
        setSt(r.data);
        setForm({ enabled: !!r.data.enabled, nomor_pengirim: r.data.nomor_pengirim || "" });
      })
      .catch(e => setLoadErr(e?.response?.data?.detail || "Gagal memuat status WhatsApp"));
  };
  useEffect(() => { loadStatus(); }, []);

  const simpanPengaturan = async () => {
    setSaving(true);
    try {
      await api.put("/wa/pengaturan", form);
      toast.success("Pengaturan WhatsApp tersimpan");
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pengaturan");
    } finally { setSaving(false); }
  };

  const kirimUji = async () => {
    if (!noHp.trim()) { toast.error("Isi dulu nomor HP tujuan"); return; }
    setSending(true); setSendResult(null);
    try {
      // Backend selalu membalas 200 dengan {ok, detail} — gagal kirim BUKAN
      // error HTTP, jadi hasil ditampilkan apa adanya tanpa menebak penyebab.
      const { data } = await api.post("/wa/tes-kirim", { no_hp: noHp.trim() });
      setSendResult(data);
      if (data.ok) toast.success(data.detail); else toast.error(data.detail);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal menjalankan kirim uji";
      setSendResult({ ok: false, detail: msg });
      toast.error(msg);
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="wa-settings-tab">
      {/* Status Fonnte */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            <MessageCircle className="w-4 h-4 text-emerald-600" /> Status WhatsApp (Fonnte)
          </p>
          <button onClick={loadStatus} data-testid="btn-wa-refresh-status"
            className="h-8 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs text-slate-600 inline-flex items-center gap-1.5">
            <RefreshCw className="w-3 h-3" /> Muat Ulang
          </button>
        </div>
        {loadErr && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800">{loadErr}</div>
        )}
        {!st && !loadErr && <LoadingState loading jumlahKartu={1} data-testid="wa-status-loading" />}
        {st && (st.aktif ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900 inline-flex items-center gap-2 w-full"
            data-testid="wa-status-aktif">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>
              WhatsApp aktif · token <span className="font-mono text-xs">{st.token_preview || "••••"}</span>
              {st.nomor_pengirim && <>{" · "}pengirim <span className="font-mono text-xs">{st.nomor_pengirim}</span></>}
            </span>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 inline-flex items-start gap-2 w-full"
            data-testid="wa-status-nonaktif">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span>{st.alasan || "WhatsApp belum dikonfigurasi."}</span>
          </div>
        ))}
        <p className="mt-3 text-xs text-slate-500">
          Kalau aktif, tautan survei CSAT otomatis dikirim ke WhatsApp pelanggan saat kasir
          membuat tautan — tanpa perlu klik tombol wa.me manual.
        </p>
      </div>

      {/* Pengaturan (toggle + nomor pengirim) — token TIDAK diatur di sini */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <p className="text-sm font-semibold text-slate-900 mb-1 inline-flex items-center gap-1.5">
          <Settings className="w-4 h-4 text-emerald-600" /> Pengaturan
        </p>
        <p className="text-xs text-slate-500 mb-3">
          Saklar darurat: matikan kalau nomor pengirim kena peringatan WhatsApp, tanpa perlu ubah .env / restart server.
        </p>
        <label className="flex items-center gap-2 text-sm text-slate-700 mb-3">
          <input type="checkbox" checked={form.enabled} disabled={!isAdmin}
            onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
            data-testid="input-wa-enabled"
            className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
          Pengiriman WhatsApp menyala
        </label>
        <label className="block mb-3">
          <span className="text-xs font-semibold text-slate-600">Nomor pengirim (untuk tampilan)</span>
          <input value={form.nomor_pengirim} disabled={!isAdmin}
            onChange={e => setForm(f => ({ ...f, nomor_pengirim: e.target.value }))}
            placeholder="08xx / 628xx — nomor device Fonnte"
            data-testid="input-wa-nomor-pengirim"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50" />
        </label>
        <button onClick={simpanPengaturan} disabled={saving || !isAdmin} data-testid="btn-wa-simpan-pengaturan"
          title={!isAdmin ? "Hanya admin/superadmin yang boleh mengubah pengaturan" : undefined}
          className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Menyimpan…" : "Simpan Pengaturan"}
        </button>
      </div>

      {/* Laporan Telegram — checklist bagian mana saja dari report harian otomatis yang dikirim */}
      {isAdmin && <TelegramReportPanel />}

      {/* Kirim pesan uji */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <p className="text-sm font-semibold text-slate-900 mb-1 inline-flex items-center gap-1.5">
          <PlugZap className="w-4 h-4 text-emerald-600" /> Kirim Pesan Uji
        </p>
        <p className="text-xs text-slate-500 mb-3">
          Kirim 1 pesan sungguhan ke nomor di bawah (mis. nomor Anda sendiri) untuk membuktikan token Fonnte benar-benar jalan.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={noHp} onChange={e => setNoHp(e.target.value)} disabled={!isAdmin}
            placeholder="08xxxxxxxxxx" data-testid="input-wa-nohp"
            className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-50" />
          <button onClick={kirimUji} disabled={sending || !isAdmin} data-testid="btn-wa-tes-kirim"
            title={!isAdmin ? "Hanya admin/superadmin yang boleh mengirim pesan uji" : undefined}
            className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60 whitespace-nowrap">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {sending ? "Mengirim…" : "Kirim Pesan Uji"}
          </button>
        </div>
        {sendResult && (
          <div data-testid="wa-test-result"
            className={`mt-3 rounded-lg border p-3 text-xs ${
              sendResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}>
            <p className="font-semibold">{sendResult.ok ? "Terkirim" : "Gagal"}</p>
            <p className="mt-0.5">{sendResult.detail}</p>
            {sendResult.target && <p className="mt-0.5">Nomor tujuan (ternormalisasi): <span className="font-mono">{sendResult.target}</span></p>}
          </div>
        )}
      </div>

      {/* Cara mengisi token — sengaja TIDAK ada form input token di sini */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 space-y-2">
        <p className="font-semibold text-slate-800">Cara mengaktifkan WhatsApp</p>
        <ol className="list-decimal ml-4 space-y-1">
          <li>Daftar device di <span className="font-mono">fonnte.com</span>, scan QR dengan nomor WhatsApp toko, salin token device-nya.</li>
          <li>Buka file <code className="font-mono bg-white border border-slate-200 rounded px-1">backend/.env</code> di server, isi <code className="font-mono bg-white border border-slate-200 rounded px-1">FONNTE_TOKEN</code>.</li>
          <li>Restart server backend, lalu klik &quot;Muat Ulang&quot; dan &quot;Kirim Pesan Uji&quot; di halaman ini.</li>
        </ol>
        <p className="text-slate-500">
          Fonnte dipilih karena murah untuk nomor Indonesia dan tidak butuh verifikasi bisnis Meta.
          Token tidak pernah ditampilkan utuh (hanya 4 karakter terakhir) dan sengaja tidak ada form
          untuk menyimpannya ke database — sama seperti kunci AI, rahasia hanya hidup di .env.
        </p>
      </div>
    </div>
  );
}

/* ================= Laporan Telegram (checklist bagian report harian) ================= */
// Label & jadwal fallback dipakai sebelum GET pertama selesai (atau kalau
// backend tidak mengirim `sections`) — supaya checklist tidak kosong/kedip.
// Enam key ini HARUS sinkron dengan REPORT_SECTIONS di
// backend/routers/telegram_bot.py.
const TELEGRAM_REPORT_FALLBACK = [
  { key: "omzet_harian", label: "Omzet harian", laporan: "Rekap Harian POS (23:00 WIB)" },
  { key: "akuisisi", label: "Akuisisi", laporan: "Rekap Harian POS (23:00 WIB)" },
  { key: "mtd", label: "MTD (bulan berjalan)", laporan: "Rekap Harian POS (23:00 WIB)" },
  { key: "per_branch", label: "Per cabang", laporan: "Rekap Harian POS (23:00 WIB)" },
  { key: "absensi", label: "Laporan absensi 23:30", laporan: "Laporan Absensi Harian (23:30 WIB)" },
  { key: "coa_selisih", label: "Selisih COA 22:00", laporan: "Laporan Selisih Kas Harian (22:00 WIB)" },
];

function TelegramReportPanel() {
  const [sections, setSections] = useState(TELEGRAM_REPORT_FALLBACK);
  const [bagian, setBagian] = useState({});
  const [loadErr, setLoadErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoadErr("");
    api.get("/telegram/report-config")
      .then(r => {
        setBagian(r.data.bagian || {});
        if (Array.isArray(r.data.sections) && r.data.sections.length) setSections(r.data.sections);
      })
      .catch(e => setLoadErr(e?.response?.data?.detail || "Gagal memuat konfigurasi laporan Telegram"));
  };
  useEffect(() => { load(); }, []);

  const simpan = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/telegram/report-config", { bagian });
      setBagian(data.bagian || bagian);
      toast.success("Konfigurasi laporan Telegram tersimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan konfigurasi laporan Telegram");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="tg-report-panel">
      <p className="text-sm font-semibold text-slate-900 mb-1 inline-flex items-center gap-1.5">
        <Bell className="w-4 h-4 text-emerald-600" /> Laporan Telegram Otomatis
      </p>
      <p className="text-xs text-slate-500 mb-3">
        Pilih bagian mana saja dari laporan harian otomatis yang benar-benar dikirim ke Telegram.
        Nonaktifkan yang tidak diperlukan tanpa mematikan seluruh laporan.
      </p>
      {loadErr && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800 mb-3">{loadErr}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
        {sections.map(s => (
          <label key={s.key}
            className="flex items-start gap-2 p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={!!bagian[s.key]}
              onChange={e => setBagian(b => ({ ...b, [s.key]: e.target.checked }))}
              data-testid={`tg-report-${s.key}`}
              className="w-4 h-4 mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
            <span>
              <span className="block text-sm font-medium text-slate-800">{s.label}</span>
              {s.laporan && <span className="block text-[11px] text-slate-400">{s.laporan}</span>}
            </span>
          </label>
        ))}
      </div>
      <button onClick={simpan} disabled={saving} data-testid="tg-report-simpan"
        className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? "Menyimpan…" : "Simpan Konfigurasi"}
      </button>
    </div>
  );
}

/* ================= Pengaturan AI ================= */
function PengaturanAITab({ isAdmin }) {
  const [st, setSt] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadStatus = () => {
    setLoadErr("");
    api.get("/ai/status")
      .then(r => setSt(r.data))
      .catch(e => setLoadErr(e?.response?.data?.detail || "Gagal memuat status AI"));
  };
  useEffect(() => { loadStatus(); }, []);

  const tesKoneksi = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/ai/test-koneksi");
      // Hasil ditampilkan apa adanya — backend sudah membungkus sukses/gagal
      // dalam {ok, pesan}, jadi UI tidak perlu menebak-nebak penyebabnya.
      setTestResult(data);
      if (data.ok) toast.success(data.pesan); else toast.error(data.pesan);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal menjalankan tes koneksi";
      setTestResult({ ok: false, pesan: msg });
      toast.error(msg);
    } finally { setTesting(false); }
  };

  const penyedia = st?.penyedia || {};
  const urutan = ["openai", "moonshot"]; // urutan tetap supaya tampilan tidak loncat-loncat

  return (
    <div className="space-y-4 max-w-3xl" data-testid="ai-settings-tab">
      {/* Status penyedia aktif */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-emerald-600" /> Status Penyedia AI
          </p>
          <button onClick={loadStatus} data-testid="btn-ai-refresh-status"
            className="h-8 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs text-slate-600 inline-flex items-center gap-1.5">
            <RefreshCw className="w-3 h-3" /> Muat Ulang
          </button>
        </div>
        {loadErr && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800">{loadErr}</div>
        )}
        {!st && !loadErr && <LoadingState loading jumlahKartu={1} data-testid="ai-status-loading" />}
        {st && (
          <>
            {st.siap ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900 inline-flex items-center gap-2 w-full"
                data-testid="ai-status-aktif">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>
                  Penyedia aktif: <b>{st.provider_label}</b>
                  {" · "}model <span className="font-mono text-xs">{penyedia[st.provider_aktif]?.model || "-"}</span>
                  {" · "}kunci <span className="font-mono text-xs">{penyedia[st.provider_aktif]?.kunci_preview || "••••"}</span>
                </span>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 inline-flex items-start gap-2 w-full"
                data-testid="ai-status-nonaktif">
                <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <span>{st.alasan || "AI belum dikonfigurasi."}</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              {urutan.map(k => {
                const p = penyedia[k];
                if (!p) return null;
                const aktif = st.provider_aktif === k;
                return (
                  <div key={k} data-testid={`ai-provider-${k}`}
                    className={`rounded-xl border p-4 ${aktif ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-slate-900">{p.label}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        p.terisi ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {p.terisi ? "Kunci terisi" : "Belum diisi"}
                      </span>
                    </div>
                    <dl className="text-xs text-slate-600 space-y-1">
                      <div className="flex justify-between gap-2">
                        <dt className="text-slate-400">Kunci API</dt>
                        <dd className="font-mono">{p.terisi ? p.kunci_preview : "—"}</dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt className="text-slate-400">Model</dt>
                        <dd className="font-mono truncate">{p.model}</dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt className="text-slate-400">Model Vision</dt>
                        <dd className="font-mono truncate">{p.model_vision}</dd>
                      </div>
                    </dl>
                    {aktif && <p className="mt-2 text-[10px] font-semibold text-emerald-700 uppercase tracking-wider">Sedang dipakai</p>}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Tes koneksi */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        <p className="text-sm font-semibold text-slate-900 mb-1 inline-flex items-center gap-1.5">
          <PlugZap className="w-4 h-4 text-emerald-600" /> Tes Koneksi
        </p>
        <p className="text-xs text-slate-500 mb-3">
          Kirim 1 permintaan super pendek ke penyedia aktif untuk membuktikan kuncinya benar-benar jalan (biaya nyaris nol).
        </p>
        <button onClick={tesKoneksi} disabled={testing || !isAdmin} data-testid="btn-ai-test"
          title={!isAdmin ? "Hanya admin/superadmin yang boleh menjalankan tes" : undefined}
          className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />}
          {testing ? "Menguji…" : "Tes Koneksi"}
        </button>
        {testResult && (
          <div data-testid="ai-test-result"
            className={`mt-3 rounded-lg border p-3 text-xs ${
              testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}>
            {testResult.ok ? (
              <>
                <p className="font-semibold">{testResult.provider_label} terhubung · model <span className="font-mono">{testResult.model}</span></p>
                {testResult.jawaban && <p className="mt-1">Jawaban model: <span className="font-mono">&quot;{testResult.jawaban}&quot;</span></p>}
              </>
            ) : (
              <p>{testResult.pesan}</p>
            )}
          </div>
        )}
      </div>

      {/* Cara mengisi kunci — sengaja TIDAK ada form input kunci di sini */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 space-y-2">
        <p className="font-semibold text-slate-800">Cara mengganti / mengisi kunci API</p>
        <ol className="list-decimal ml-4 space-y-1">
          <li>Buka file <code className="font-mono bg-white border border-slate-200 rounded px-1">backend/.env</code> di server.</li>
          <li>Isi <code className="font-mono bg-white border border-slate-200 rounded px-1">OPENAI_API_KEY</code> atau <code className="font-mono bg-white border border-slate-200 rounded px-1">MOONSHOT_API_KEY</code> (boleh dua-duanya — OpenAI diprioritaskan saat mode auto).</li>
          <li>Restart server backend, lalu klik &quot;Muat Ulang&quot; dan &quot;Tes Koneksi&quot; di halaman ini.</li>
        </ol>
        <p className="text-slate-500">
          Kunci tidak pernah ditampilkan utuh di halaman ini (hanya 4 karakter terakhir), dan sengaja
          tidak ada form untuk menyimpan kunci ke database — rahasia yang disimpan di DB tanpa desain
          enkripsi matang justru menambah titik bocor.
        </p>
      </div>
    </div>
  );
}

/* ================= Branding (Logo & Nama) ================= */
function BrandingTab({ isAdmin }) {
  const [b, setB] = useState({
    app_name: "EppleHub Operational System", header_logo_url: "", footer_text: "", footer_logo_url: "", show_footer: true,
    login_subtitle_under_logo: "Sistem Informasi Karyawan",
    login_card_title: "Selamat datang kembali",
    login_card_subtitle: "Masuk untuk mengelola data karyawan, payroll, cuti dan performa.",
    login_hero_title: "Kelola tim EppleHub dengan lebih rapi dan cepat.",
    login_hero_subtitle: "Dashboard karyawan, payroll otomatis, dan pemantauan performa dalam satu tempat.",
    login_hero_image_url: "",
    login_show_demo_accounts: false,
    login_demo_notice: "",
    login_footer_note: "",
    login_notice_banner: "",
    login_notice_type: "info",
    login_button_label: "Masuk",
    browser_title: "EppleHub Operational System — Sistem Informasi Karyawan",
    favicon_url: "",
    og_image_url: "",
    og_description: "Sistem Informasi Karyawan EppleHub — HRIS, POS, Payroll & Analitik dalam satu aplikasi.",
    pwa_name: "EppleHub Operational System",
    pwa_short_name: "EppleHub",
    nama_toko_sendiri: "EppleHub",
  });
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  useEffect(() => { api.get("/config/branding").then(r => setB(prev => ({ ...prev, ...r.data }))); }, []);
  const onFile = (key) => (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 500 * 1024) { toast.error("Ukuran maksimal 500KB — pakai gambar lebih kecil"); return; }
    const reader = new FileReader();
    reader.onload = () => setB(s => ({ ...s, [key]: reader.result }));
    reader.readAsDataURL(f);
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/config/branding", b);
      // Broadcast ke AuthContext biar tab title + favicon update tanpa refresh
      window.dispatchEvent(new CustomEvent("branding-updated", { detail: data }));
      toast.success("Branding tersimpan — tab browser sudah diperbarui");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-6" data-testid="branding-tab">
      <div>
        <p className="text-sm font-semibold text-slate-900 mb-1">Identitas Aplikasi</p>
        <p className="text-xs text-slate-500">Ubah nama & logo yang tampil di header (pojok kiri) dan footer aplikasi. Berlaku untuk semua user setelah refresh.</p>
      </div>

      {/* ============ Browser Tab (Favicon + Title) ============ */}
      <div className="border-t border-slate-100 pt-4">
        <p className="text-sm font-semibold text-slate-900 mb-1">Tab Browser (Favicon &amp; Judul)</p>
        <p className="text-xs text-slate-500 mb-3">Icon kecil dan teks yang tampil di tab browser Chrome/Safari/Firefox. Berubah live begitu disimpan.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Judul Tab Browser</span>
            <input value={b.browser_title || ""} onChange={e => setB(s => ({ ...s, browser_title: e.target.value }))}
              data-testid="branding-browser-title"
              placeholder="EppleHub Operational System — Sistem Informasi Karyawan"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <p className="text-[10px] text-slate-400 mt-1">Tampil di tab browser + hasil bookmark. Sisip nama perusahaanmu.</p>
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Favicon (icon tab, upload PNG/ICO 32×32 atau 64×64, max 100KB)</span>
            <div className="mt-1 flex items-center gap-3">
              {b.favicon_url ? (
                <div className="relative">
                  <div className="h-12 w-12 rounded bg-slate-100 border border-slate-200 grid place-items-center overflow-hidden">
                    <img src={b.favicon_url} alt="favicon preview" className="max-h-10 max-w-10 object-contain" />
                  </div>
                  <button onClick={() => setB(s => ({ ...s, favicon_url: "" }))}
                    data-testid="branding-clear-favicon"
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-500 text-white grid place-items-center text-[10px]">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="h-12 w-12 bg-slate-100 border border-dashed border-slate-300 rounded grid place-items-center text-slate-400">
                  <ImageIcon className="w-5 h-5" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input value={b.favicon_url?.startsWith("data:") ? "" : (b.favicon_url || "")}
                  onChange={e => setB(s => ({ ...s, favicon_url: e.target.value }))}
                  data-testid="branding-favicon-url"
                  placeholder="https://example.com/favicon.png"
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 text-xs" />
                <label className="h-8 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 text-xs cursor-pointer">
                  <Upload className="w-3.5 h-3.5" /> Upload PNG/ICO
                  <input type="file" accept="image/png,image/x-icon,image/vnd.microsoft.icon,image/svg+xml"
                    onChange={(e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      if (f.size > 100 * 1024) { toast.error("Favicon max 100KB"); return; }
                      const reader = new FileReader();
                      reader.onload = () => setB(s => ({ ...s, favicon_url: reader.result }));
                      reader.readAsDataURL(f);
                    }} className="hidden" />
                </label>
                <p className="text-[10px] text-slate-400">Tip: pakai PNG 64×64 background transparan supaya tajam di dark/light mode.</p>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* ============ Nama & Logo Aplikasi ============ */}
      <div className="border-t border-slate-100 pt-4">
        <p className="text-sm font-semibold text-slate-900 mb-1">Social Media Preview &amp; PWA</p>
        <p className="text-xs text-slate-500 mb-3">Tampilan saat link di-share (WhatsApp/Slack/FB) + nama saat di-install sebagai app di HP.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Deskripsi Preview (WhatsApp/Slack/FB)</span>
            <textarea value={b.og_description || ""} onChange={e => setB(s => ({ ...s, og_description: e.target.value }))}
              data-testid="branding-og-desc"
              rows={2}
              placeholder="Sistem Informasi EppleHub — HRIS, POS, Payroll..."
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
            <p className="text-[10px] text-slate-400 mt-1">Muncul di bawah judul link saat share. Max 200 karakter untuk hasil terbaik.</p>
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">URL Gambar OG (1200×630, WhatsApp/Slack preview)</span>
            <input value={b.og_image_url || ""} onChange={e => setB(s => ({ ...s, og_image_url: e.target.value }))}
              data-testid="branding-og-image"
              placeholder="https://epplehub.com/og-preview.jpg"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <p className="text-[10px] text-slate-400 mt-1">Kosongkan → pakai favicon sebagai fallback. Rekomendasi: 1200×630px JPG/PNG (Facebook OG standard).</p>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Nama PWA Lengkap (installer)</span>
            <input value={b.pwa_name || ""} onChange={e => setB(s => ({ ...s, pwa_name: e.target.value }))}
              data-testid="branding-pwa-name"
              placeholder="EppleHub Operational System"
              maxLength={45}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Nama Pendek PWA (max 12 chr, home screen)</span>
            <input value={b.pwa_short_name || ""} onChange={e => setB(s => ({ ...s, pwa_short_name: e.target.value }))}
              data-testid="branding-pwa-short"
              placeholder="EppleHub"
              maxLength={12}
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
        </div>
      </div>

      {/* ============ Nama & Logo Sidebar ============ */}
      <div className="border-t border-slate-100 pt-4">
        <p className="text-sm font-semibold text-slate-900 mb-3">Nama &amp; Logo Sidebar</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Nama Aplikasi *</span>
          <input value={b.app_name} onChange={e => setB(s => ({ ...s, app_name: e.target.value }))} data-testid="branding-app-name"
            className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Nama Penjualan Toko Sendiri</span>
          <input value={b.nama_toko_sendiri || ""} onChange={e => setB(s => ({ ...s, nama_toko_sendiri: e.target.value }))}
            data-testid="branding-nama-toko-sendiri" placeholder="EppleHub" maxLength={40}
            className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          {/* Muncul di menu Price Protection & Claim MDR sebagai lawan "Dealer Partner",
              dan ikut tercetak di rekap Excel yang dikirim ke supplier. */}
          <p className="text-[10px] text-slate-400 mt-1">Dipakai di Price Protection &amp; Claim MDR sebagai lawan "Dealer Partner". Kosong = EppleHub.</p>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Logo Header (PNG/JPG, max 500KB)</span>
          <div className="mt-1 flex items-center gap-3">
            {b.header_logo_url ? (
              <div className="relative">
                <img src={b.header_logo_url} alt="preview" className="w-14 h-14 rounded-lg object-cover border border-slate-200" />
                <button onClick={() => setB(s => ({ ...s, header_logo_url: "" }))} data-testid="branding-clear-header-logo"
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-500 text-white grid place-items-center text-[10px]"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <div className="w-14 h-14 rounded-lg bg-slate-100 border border-dashed border-slate-300 grid place-items-center text-slate-400">
                <ImageIcon className="w-5 h-5" />
              </div>
            )}
            <label data-testid="branding-header-logo-upload"
              className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 text-xs cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> Upload Logo
              <input type="file" accept="image/*" onChange={onFile("header_logo_url")} className="hidden" />
            </label>
          </div>
        </label>
      </div>
      <div className="border-t border-slate-100 pt-5">
        <p className="text-sm font-semibold text-slate-900 mb-3">Footer</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Teks Footer</span>
            <input value={b.footer_text} onChange={e => setB(s => ({ ...s, footer_text: e.target.value }))} data-testid="branding-footer-text"
              placeholder="© 2026 EppleHub. All rights reserved."
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Logo Footer (opsional, max 500KB)</span>
            <div className="mt-1 flex items-center gap-3">
              {b.footer_logo_url ? (
                <div className="relative">
                  <img src={b.footer_logo_url} alt="preview" className="h-10 object-contain border border-slate-200 rounded px-2" />
                  <button onClick={() => setB(s => ({ ...s, footer_logo_url: "" }))} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-500 text-white grid place-items-center"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <div className="h-10 w-16 bg-slate-100 border border-dashed border-slate-300 rounded grid place-items-center text-slate-400">
                  <ImageIcon className="w-4 h-4" />
                </div>
              )}
              <label className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 text-xs cursor-pointer">
                <Upload className="w-3.5 h-3.5" /> Upload
                <input type="file" accept="image/*" onChange={onFile("footer_logo_url")} className="hidden" />
              </label>
            </div>
          </label>
          <label className="inline-flex items-center gap-2 mt-2">
            <input type="checkbox" checked={b.show_footer !== false} onChange={e => setB(s => ({ ...s, show_footer: e.target.checked }))} data-testid="branding-show-footer"
              className="w-4 h-4 accent-emerald-600" />
            <span className="text-sm text-slate-700">Tampilkan footer di semua halaman</span>
          </label>
        </div>
      </div>
      {isAdmin && (
        <div className="flex justify-end border-t border-slate-100 pt-4">
          <button onClick={save} disabled={saving} data-testid="branding-save"
            className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan
          </button>
        </div>
      )}

      {/* ============ Kustomisasi Halaman Login ============ */}
      <div className="border-t border-slate-100 pt-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Kustomisasi Halaman Login</p>
            <p className="text-xs text-slate-500">Edit judul, subtitle, hero image, tombol, dan notifikasi banner di halaman /login. Semua field opsional.</p>
          </div>
          <a href="/login" target="_blank" rel="noopener noreferrer"
            data-testid="branding-preview-login"
            className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium">
            Preview →
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Subtitle di bawah nama app (dekat logo)</span>
            <input value={b.login_subtitle_under_logo} onChange={e => setB(s => ({ ...s, login_subtitle_under_logo: e.target.value }))}
              data-testid="branding-login-subtitle-logo"
              placeholder="Sistem Informasi Karyawan"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Label Tombol Login</span>
            <input value={b.login_button_label} onChange={e => setB(s => ({ ...s, login_button_label: e.target.value }))}
              data-testid="branding-login-btn"
              placeholder="Masuk / Login / Sign In"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Judul Kartu Login (H2)</span>
            <input value={b.login_card_title} onChange={e => setB(s => ({ ...s, login_card_title: e.target.value }))}
              data-testid="branding-login-title"
              placeholder="Selamat datang kembali"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Subtitle Kartu Login</span>
            <textarea value={b.login_card_subtitle} onChange={e => setB(s => ({ ...s, login_card_subtitle: e.target.value }))}
              data-testid="branding-login-subtitle"
              rows={2}
              placeholder="Masuk untuk mengelola data karyawan…"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Judul Hero (Panel Kanan)</span>
            <textarea value={b.login_hero_title} onChange={e => setB(s => ({ ...s, login_hero_title: e.target.value }))}
              data-testid="branding-login-hero-title"
              rows={2}
              placeholder="Kelola tim dengan lebih rapi & cepat"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Subtitle Hero (Panel Kanan)</span>
            <textarea value={b.login_hero_subtitle} onChange={e => setB(s => ({ ...s, login_hero_subtitle: e.target.value }))}
              data-testid="branding-login-hero-subtitle"
              rows={2}
              placeholder="Dashboard, payroll otomatis…"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Gambar Hero (URL atau upload, max 500KB)</span>
            <div className="mt-1 flex items-center gap-3">
              {b.login_hero_image_url ? (
                <div className="relative">
                  <img src={b.login_hero_image_url} alt="hero preview"
                    className="h-16 w-24 object-cover rounded-lg border border-slate-200" />
                  <button onClick={() => setB(s => ({ ...s, login_hero_image_url: "" }))}
                    data-testid="branding-clear-hero"
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-500 text-white grid place-items-center text-[10px]">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="h-16 w-24 bg-slate-100 border border-dashed border-slate-300 rounded-lg grid place-items-center text-slate-400">
                  <ImageIcon className="w-5 h-5" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input value={b.login_hero_image_url?.startsWith("data:") ? "" : (b.login_hero_image_url || "")}
                  onChange={e => setB(s => ({ ...s, login_hero_image_url: e.target.value }))}
                  data-testid="branding-hero-url"
                  placeholder="https://images.unsplash.com/…"
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 text-xs" />
                <label className="h-8 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 text-xs cursor-pointer">
                  <Upload className="w-3.5 h-3.5" /> Upload
                  <input type="file" accept="image/*" onChange={onFile("login_hero_image_url")} className="hidden" />
                </label>
              </div>
            </div>
          </label>

          {/* Banner Pengumuman */}
          <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-2">
            <p className="text-xs font-semibold text-slate-700 mb-2">Banner Pengumuman (opsional, tampil di atas form)</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <label className="block md:col-span-3">
                <textarea value={b.login_notice_banner} onChange={e => setB(s => ({ ...s, login_notice_banner: e.target.value }))}
                  data-testid="branding-login-notice"
                  rows={2}
                  placeholder="Contoh: Maintenance 30 Nov 22:00-23:00 WIB. Kosongkan untuk sembunyikan."
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-slate-500">Tipe Banner</span>
                <select value={b.login_notice_type} onChange={e => setB(s => ({ ...s, login_notice_type: e.target.value }))}
                  data-testid="branding-login-notice-type"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm">
                  <option value="info">Info (biru)</option>
                  <option value="warning">Warning (kuning)</option>
                  <option value="success">Success (hijau)</option>
                </select>
              </label>
            </div>
          </div>

          <label className="block md:col-span-2">
            <span className="text-xs font-semibold text-slate-600">Catatan Kaki (di bawah tombol Lupa Password)</span>
            <textarea value={b.login_footer_note} onChange={e => setB(s => ({ ...s, login_footer_note: e.target.value }))}
              data-testid="branding-login-footer-note"
              rows={2}
              placeholder="Contoh: Butuh bantuan? Hubungi IT di 0812-3456-7890"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </label>

          {/* Akun Demo Toggle */}
          <div className="md:col-span-2 bg-slate-50 rounded-lg p-3 border border-slate-200">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!b.login_show_demo_accounts}
                onChange={e => setB(s => ({ ...s, login_show_demo_accounts: e.target.checked }))}
                data-testid="branding-show-demo"
                className="w-4 h-4 accent-emerald-600" />
              <span className="text-sm font-medium text-slate-700">Tampilkan kotak "Akun Demo" di halaman login</span>
            </label>
            <p className="text-[10px] text-slate-500 mt-1 ml-6">Nonaktifkan di lingkungan produksi. Isi teks kustom di bawah untuk override daftar default.</p>
            {b.login_show_demo_accounts && (
              <textarea value={b.login_demo_notice} onChange={e => setB(s => ({ ...s, login_demo_notice: e.target.value }))}
                data-testid="branding-demo-notice"
                rows={3}
                placeholder="Kosongkan = pakai default (superadmin/admin/karyawan). Isi = override dengan teks kustom (support multi-line)."
                className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 text-xs font-mono" />
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="flex justify-end mt-4">
            <button onClick={save} disabled={saving} data-testid="branding-login-save"
              className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan Perubahan
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ================= Grade Gaji ================= */
function GradesTab({ isSuper, isAdmin }) {
  const [grades, setGrades] = useState([]);
  const [newGrade, setNewGrade] = useState({ grade_jabatan: "", gaji_pokok: 0, tunj_jabatan: 0, tunj_makan: 0, kualifikasi: "", status_kerja: "PKWT" });

  const load = () => api.get("/grades").then(r => setGrades(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newGrade.grade_jabatan) { toast.error("Grade wajib diisi"); return; }
    try { await api.post("/grades", newGrade); toast.success("Grade ditambahkan"); setNewGrade({ grade_jabatan: "", gaji_pokok: 0, tunj_jabatan: 0, tunj_makan: 0, kualifikasi: "", status_kerja: "PKWT" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (id) => { if (!confirm("Hapus grade ini?")) return; await api.delete(`/grades/${id}`); load(); };
  // Urutan bawaan dipertahankan sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState();

  const download = async () => {
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/import-templates/grades`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Gagal download template"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "Template_GradeGaji.xlsx";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/grades/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import: ${data.created} baru, ${data.updated} update${data.errors.length ? `, ${data.errors.length} error` : ""}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal import"); }
    e.target.value = "";
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex flex-wrap gap-2 items-center justify-between">
        <p className="text-sm font-semibold text-slate-900">Daftar Grade Gaji ({grades.length})</p>
        <div className="flex gap-2">
          <button onClick={download} data-testid="btn-template-grades"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium">
            <Download className="w-3.5 h-3.5" /> Template
          </button>
          {isSuper && (
            <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> Import
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={upload} data-testid="btn-import-grades" />
            </label>
          )}
        </div>
      </div>
      {isSuper && (
        <div className="p-4 border-b border-slate-100 bg-slate-50/40">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Tambah Grade Baru</p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <input placeholder="Grade" value={newGrade.grade_jabatan} onChange={e => setNewGrade(s => ({ ...s, grade_jabatan: e.target.value }))} className="col-span-2 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <InputRupiah placeholder="Gaji Pokok" value={newGrade.gaji_pokok || ""} onChange={n => setNewGrade(s => ({ ...s, gaji_pokok: n }))} className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <InputRupiah placeholder="Tunj Jabatan" value={newGrade.tunj_jabatan || ""} onChange={n => setNewGrade(s => ({ ...s, tunj_jabatan: n }))} className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <InputRupiah placeholder="Tunj Makan" value={newGrade.tunj_makan || ""} onChange={n => setNewGrade(s => ({ ...s, tunj_makan: n }))} className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <button onClick={add} className="h-10 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm inline-flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Tambah</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortTh label="Grade Jabatan" sortKeyName="grade_jabatan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="grades-sort-nama" />
              <SortTh label="Gaji Pokok" sortKeyName="gaji_pokok" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="grades-sort-pokok" />
              <SortTh label="Tunj. Jabatan" sortKeyName="tunj_jabatan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="grades-sort-tunjjab" />
              <SortTh label="Tunj. Makan" sortKeyName="tunj_makan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="grades-sort-tunjmakan" />
              <SortTh label="Total Tetap" sortKeyName="total" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="grades-sort-total" />
              <th className="text-left py-3 px-4">Status</th>
              <th className="text-right py-3 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {sortRows(grades, sortKey, sortDir, {
              total: (g) => g.gaji_pokok + g.tunj_jabatan + g.tunj_makan,
            }).map(g => (
              <tr key={g.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4 font-medium text-slate-900">{g.grade_jabatan}</td>
                <td className="py-3 px-4 text-right font-mono text-xs">{idr(g.gaji_pokok)}</td>
                <td className="py-3 px-4 text-right font-mono text-xs">{idr(g.tunj_jabatan)}</td>
                <td className="py-3 px-4 text-right font-mono text-xs">{idr(g.tunj_makan)}</td>
                <td className="py-3 px-4 text-right font-mono text-xs font-semibold">{idr(g.gaji_pokok + g.tunj_jabatan + g.tunj_makan)}</td>
                <td className="py-3 px-4"><span className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700">{g.status_kerja}</span></td>
                <td className="py-3 px-4 text-right">
                  {isSuper && <button onClick={() => del(g.id)} className="p-2 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
            {grades.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-sm text-slate-500">Belum ada grade</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= Shift Kerja ================= */
function ShiftsTab({ isAdmin }) {
  const [shifts, setShifts] = useState([]);
  const [nS, setNS] = useState({ name: "", jam_masuk: "08:00", jam_pulang: "17:00", toleransi_menit: 15, catatan: "", is_active: true });
  const [assignModal, setAssignModal] = useState(null);
  const load = () => api.get("/shifts").then(r => setShifts(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!nS.name || !nS.jam_masuk || !nS.jam_pulang) { toast.error("Nama & jam wajib"); return; }
    try { await api.post("/shifts", nS); toast.success("Shift ditambahkan"); setNS({ name: "", jam_masuk: "08:00", jam_pulang: "17:00", toleransi_menit: 15, catatan: "", is_active: true }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (id) => { if (!confirm("Hapus shift? Semua roster yang pakai shift ini akan ikut terhapus.")) return; await api.delete(`/shifts/${id}`); load(); };
  const toggle = async (s) => { await api.patch(`/shifts/${s.id}`, { is_active: !s.is_active }); load(); };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">Daftar Shift ({shifts.length})</p>
          <p className="text-xs text-slate-500">Toleransi menit = keterlambatan yang dimaafkan sebelum dihitung telat.</p>
        </div>
      </div>
      {isAdmin && (
        <div className="p-4 border-b border-slate-100 bg-slate-50/40">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <input placeholder="Nama Shift (Pagi/Siang/Malam)" value={nS.name} onChange={e => setNS(s => ({ ...s, name: e.target.value }))} className="col-span-2 h-10 px-3 rounded-lg border border-slate-200 text-sm" data-testid="shift-name" />
            <input type="time" value={nS.jam_masuk} onChange={e => setNS(s => ({ ...s, jam_masuk: e.target.value }))} className="h-10 px-3 rounded-lg border border-slate-200 text-sm" data-testid="shift-masuk" />
            <input type="time" value={nS.jam_pulang} onChange={e => setNS(s => ({ ...s, jam_pulang: e.target.value }))} className="h-10 px-3 rounded-lg border border-slate-200 text-sm" data-testid="shift-pulang" />
            <input type="number" placeholder="Toleransi (mnt)" value={nS.toleransi_menit} onChange={e => setNS(s => ({ ...s, toleransi_menit: parseInt(e.target.value) || 0 }))} className="h-10 px-3 rounded-lg border border-slate-200 text-sm" data-testid="shift-tol" />
            <button onClick={add} data-testid="shift-add" className="h-10 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm inline-flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Tambah</button>
          </div>
          <input placeholder="Catatan (opsional)" value={nS.catatan} onChange={e => setNS(s => ({ ...s, catatan: e.target.value }))} className="mt-2 w-full h-9 px-3 rounded-lg border border-slate-200 text-xs" />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <th className="text-left py-3 px-4">Nama</th>
            <th className="text-center py-3 px-4">Masuk</th>
            <th className="text-center py-3 px-4">Pulang</th>
            <th className="text-center py-3 px-4">Toleransi</th>
            <th className="text-left py-3 px-4">Catatan</th>
            <th className="text-center py-3 px-4">Aktif</th>
            <th className="text-right py-3 px-4"></th>
          </tr></thead>
          <tbody>
            {shifts.map(s => (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4 font-medium text-slate-900 inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" /> {s.name}</td>
                <td className="py-3 px-4 text-center font-mono text-emerald-700 font-semibold">{s.jam_masuk}</td>
                <td className="py-3 px-4 text-center font-mono text-rose-700 font-semibold">{s.jam_pulang}</td>
                <td className="py-3 px-4 text-center">{s.toleransi_menit} mnt</td>
                <td className="py-3 px-4 text-xs text-slate-500">{s.catatan || "-"}</td>
                <td className="py-3 px-4 text-center">
                  <button onClick={() => toggle(s)} data-testid={`shift-toggle-${s.id}`} className={`px-2 py-0.5 rounded-full text-xs ${s.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.is_active ? "Aktif" : "Nonaktif"}</button>
                </td>
                <td className="py-3 px-4 text-right">
                  {isAdmin && (
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setAssignModal(s)} data-testid={`shift-assign-${s.id}`}
                        title="Kelola karyawan pada shift ini"
                        className="px-2 py-1 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs inline-flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" /> Kelola Karyawan
                      </button>
                      <button onClick={() => del(s.id)} className="p-2 hover:bg-rose-50 rounded-lg text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {shifts.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-sm text-slate-500">Belum ada shift. Tambahkan minimal 1 (mis. Pagi 08:00-17:00).</td></tr>}
          </tbody>
        </table>
      </div>
      {assignModal && <ShiftAssignModal shift={assignModal} onClose={() => setAssignModal(null)} onSaved={load} />}
    </div>
  );
}

/* ---- Bulk assign karyawan ke sebuah shift ---- */
function ShiftAssignModal({ shift, onClose, onSaved }) {
  const [data, setData] = useState({ employees: [] });
  const [checked, setChecked] = useState(new Set());
  const [q, setQ] = useState("");
  const [branch, setBranch] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/shifts/${shift.id}/employees`).then(r => {
      setData(r.data);
      setChecked(new Set(r.data.employees.filter(e => e.assigned).map(e => e.id)));
    });
  }, [shift.id]);
  const toggle = (id) => setChecked(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const filtered = (data.employees || []).filter(e => {
    if (branch && e.branch !== branch) return false;
    if (q && !((e.nama || "").toLowerCase().includes(q.toLowerCase()) || (e.grade_jabatan || "").toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const branches = [...new Set((data.employees || []).map(e => e.branch).filter(Boolean))].sort();
  const selectAll = () => setChecked(new Set(filtered.map(e => e.id).concat([...checked])));
  const clearFiltered = () => {
    const filteredIds = new Set(filtered.map(e => e.id));
    setChecked(prev => new Set([...prev].filter(id => !filteredIds.has(id))));
  };
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/shifts/${shift.id}/assign-employees`, { employee_ids: [...checked] });
      toast.success(`${r.data.total_now} karyawan diassign · ${r.data.unassigned} dilepas`);
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="shift-assign-modal">
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="font-display font-semibold text-slate-900 inline-flex items-center gap-2">
              <Clock className="w-4 h-4 text-emerald-600" /> Kelola Karyawan · Shift <span className="text-emerald-700">{shift.name}</span>
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {shift.jam_masuk} – {shift.jam_pulang} · toleransi {shift.toleransi_menit}m ·
              Terpilih: <b className="text-emerald-700 font-mono">{checked.size}</b> dari {data.employees?.length || 0} karyawan
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 border-b border-slate-100 flex flex-wrap gap-2 items-center">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Cari nama/jabatan..." data-testid="shift-assign-search"
            className="h-9 px-3 rounded-lg border border-slate-200 text-sm flex-1 min-w-[180px]" />
          <select value={branch} onChange={e => setBranch(e.target.value)} data-testid="shift-assign-branch"
            className="h-9 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="">Semua Branch</option>
            {branches.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <button onClick={selectAll} data-testid="shift-assign-selectall"
            className="h-9 px-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs">Pilih semua tampil</button>
          <button onClick={clearFiltered}
            className="h-9 px-3 rounded-lg border border-slate-200 text-xs">Lepas semua tampil</button>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-slate-100">
          {filtered.map(e => (
            <label key={e.id} data-testid={`shift-assign-emp-${e.id}`}
              className={`flex items-center gap-3 px-5 py-2.5 cursor-pointer hover:bg-slate-50 ${checked.has(e.id) ? "bg-emerald-50/40" : ""}`}>
              <input type="checkbox" checked={checked.has(e.id)} onChange={() => toggle(e.id)}
                className="w-4 h-4 accent-emerald-600" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 text-sm truncate">{e.nama}</p>
                <p className="text-xs text-slate-500 truncate">{e.grade_jabatan || "-"} · {e.branch || "-"}</p>
              </div>
              {e.default_shift_id && e.default_shift_id !== shift.id && (
                <span title="Karyawan ini sudah punya shift default lain" className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">shift lain</span>
              )}
              {checked.has(e.id) && <Check className="w-4 h-4 text-emerald-600" />}
            </label>
          ))}
          {filtered.length === 0 && <p className="text-center py-8 text-sm text-slate-500">Tidak ada karyawan sesuai filter.</p>}
        </div>
        <div className="border-t border-slate-100 px-6 py-3 flex items-center justify-between">
          <p className="text-xs text-slate-500">
            Karyawan yang tidak dicentang akan di-<i>unassign</i> dari shift ini.
          </p>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
            <button onClick={save} disabled={saving} data-testid="shift-assign-save"
              className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan Assign
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= Roster (Assign shift per karyawan per tanggal) ================= */
function RosterTab({ isAdmin }) {
  const BRANCHES = useCabangOptions();
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [date, setDate] = useState(hariIniISO());
  const [branch, setBranch] = useState("");
  const [roster, setRoster] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/employees").then(r => setEmployees((r.data || []).filter(e => e.is_active !== false).sort((a, b) => a.nama.localeCompare(b.nama))));
    api.get("/shifts").then(r => setShifts((r.data || []).filter(s => s.is_active !== false)));
  }, []);

  const loadRoster = () => api.get(`/roster?date=${date}`).then(r => setRoster(r.data || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRoster(); }, [date]);

  const rosterMap = useMemo(() => Object.fromEntries(roster.map(r => [r.employee_id, r.shift_id])), [roster]);

  const filtered = useMemo(() =>
    branch ? employees.filter(e => e.branch === branch) : employees, [employees, branch]);

  const changeShift = async (empId, shiftId) => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      if (!shiftId) {
        await api.delete(`/roster?employee_id=${empId}&date=${date}`);
      } else {
        await api.post("/roster", { entries: [{ employee_id: empId, date, shift_id: shiftId }] });
      }
      loadRoster();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  const setDefault = async (empId, shiftId) => {
    if (!isAdmin) return;
    try {
      await api.patch(`/employees/${empId}`, { default_shift_id: shiftId || null });
      toast.success("Default shift disimpan");
      const r = await api.get("/employees");
      setEmployees((r.data || []).filter(e => e.is_active !== false).sort((a, b) => a.nama.localeCompare(b.nama)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100 flex flex-wrap gap-3 items-center">
        <div>
          <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-emerald-600" /> Roster Harian</p>
          <p className="text-xs text-slate-500">Assign shift kerja ke masing-masing karyawan pada tanggal tertentu. Kosongkan = pakai shift default.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <DatePicker value={date} onChange={setDate} data-testid="roster-date" />
          <select value={branch} onChange={e => setBranch(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
            <option value="">Semua Branch</option>
            {BRANCHES.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      </div>
      {shifts.length === 0 ? (
        <div className="p-12 text-center text-sm text-slate-500">
          <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          Tambahkan minimal 1 shift dulu di tab &quot;Shift Kerja&quot;.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <th className="text-left py-3 px-4">Karyawan</th>
              <th className="text-left py-3 px-4">Branch</th>
              <th className="text-left py-3 px-4">Default Shift</th>
              <th className="text-left py-3 px-4">Roster {date}</th>
            </tr></thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">{e.nama}</p>
                    <p className="text-xs text-slate-500 font-mono">{e.nik}</p>
                  </td>
                  <td className="py-3 px-4 text-slate-700">{e.branch || "-"}</td>
                  <td className="py-3 px-4">
                    <select value={e.default_shift_id || ""} onChange={ev => setDefault(e.id, ev.target.value)}
                      disabled={!isAdmin} data-testid={`default-shift-${e.id}`}
                      className="h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                      <option value="">— tidak set —</option>
                      {shifts.map(s => <option key={s.id} value={s.id}>{`${s.name} (${s.jam_masuk}-${s.jam_pulang})`}</option>)}
                    </select>
                  </td>
                  <td className="py-3 px-4">
                    <select value={rosterMap[e.id] || ""} onChange={ev => changeShift(e.id, ev.target.value)}
                      disabled={!isAdmin || saving} data-testid={`roster-${e.id}`}
                      className="h-9 px-2 rounded border border-slate-200 text-xs bg-emerald-50">
                      <option value="">— pakai default —</option>
                      {shifts.map(s => <option key={s.id} value={s.id}>{`${s.name} (${s.jam_masuk}-${s.jam_pulang})`}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-sm text-slate-500">Tidak ada karyawan</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ================= Hari Libur ================= */
function HolidaysTab({ isAdmin }) {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({ date: hariIniISO(), name: "", scope: "global", employee_id: "" });

  const load = () => api.get(`/holidays?year=${year}`).then(r => setItems(r.data || []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [year]);
  useEffect(() => {
    api.get("/employees").then(r => setEmployees((r.data || []).filter(e => e.is_active !== false).sort((a, b) => a.nama.localeCompare(b.nama))));
  }, []);

  // Jadwal off biasanya diisi beberapa tanggal sekaligus untuk satu orang
  // (brief owner 2 Sep 2026). Tanggal dikumpulkan dulu di sini, baru dikirim
  // sekali — bukan satu permintaan per tanggal, supaya tidak ada keadaan
  // "separuh tersimpan" kalau salah satunya ditolak.
  const [tanggalDipilih, setTanggalDipilih] = useState([]);
  const tambahTanggal = () => {
    if (!form.date) { toast.error("Pilih tanggal dulu"); return; }
    setTanggalDipilih(t => t.includes(form.date) ? t : [...t, form.date].sort());
  };
  const buangTanggal = (d) => setTanggalDipilih(t => t.filter(x => x !== d));

  const add = async () => {
    // Tanggal di pemilih SELALU ikut, bukan hanya saat daftarnya kosong.
    // Kalau tidak, orang yang memilih tanggal terakhir lalu langsung menekan
    // Simpan (lupa menekan "+") kehilangan tanggal itu diam-diam — dan
    // notifikasinya tetap bilang berhasil, jadi tidak ada yang sadar.
    const dates = [...new Set([...tanggalDipilih, ...(form.date ? [form.date] : [])])].sort();
    if (!dates.length) { toast.error("Pilih tanggal dulu"); return; }
    if (form.scope === "employee" && !form.employee_id) { toast.error("Pilih karyawan"); return; }
    // "OFF" adalah keterangan bawaan jadwal libur karyawan — mengetiknya ulang
    // untuk tiap tanggal menambah kerja tanpa menambah informasi. Libur GLOBAL
    // tetap wajib bernama; "OFF" untuk semua orang tidak menjelaskan apa pun.
    const nama = (form.name || "").trim() || (form.scope === "employee" ? "OFF" : "");
    if (!nama) { toast.error("Nama libur wajib (mis. HUT RI)"); return; }
    try {
      const r = await api.post("/holidays/bulk", {
        dates, name: nama, scope: form.scope,
        employee_id: form.scope === "employee" ? form.employee_id : null,
      });
      toast.success(`${r.data?.tersimpan ?? dates.length} hari libur disimpan`);
      setForm(s => ({ ...s, name: "", employee_id: "" }));
      setTanggalDipilih([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (id) => { if (!confirm("Hapus hari libur ini?")) return; await api.delete(`/holidays/${id}`); load(); };

  const globalItems = items.filter(h => h.scope === "global");
  const empItems = items.filter(h => h.scope === "employee");
  const empName = (id) => (employees.find(e => e.id === id)?.nama) || id;

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5"><PartyPopper className="w-4 h-4 text-emerald-600" /> Tambah Hari Libur</p>
          <div className="text-xs">
            <label className="mr-1 text-slate-500">Tahun:</label>
            <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="h-8 px-2 rounded border border-slate-200 text-xs bg-white">
              {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        {isAdmin && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <div className="flex gap-1">
              <div className="flex-1"><DatePicker value={form.date} onChange={v => setForm(s => ({ ...s, date: v }))} data-testid="hol-date" /></div>
              <button type="button" onClick={tambahTanggal} data-testid="hol-add-date"
                title="Kumpulkan tanggal ini — bisa beberapa sekaligus"
                className="h-10 px-2.5 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-semibold shrink-0">+</button>
            </div>
            <input placeholder={form.scope === "employee" ? "Keterangan (kosong = OFF)" : "Nama (mis. HUT RI)"} value={form.name} onChange={e => setForm(s => ({ ...s, name: e.target.value }))} data-testid="hol-name" className="col-span-2 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <select value={form.scope} onChange={e => setForm(s => ({ ...s, scope: e.target.value }))} data-testid="hol-scope" className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
              <option value="global">Global (semua)</option>
              <option value="employee">Per karyawan</option>
            </select>
            {form.scope === "employee" ? (
              <select value={form.employee_id} onChange={e => setForm(s => ({ ...s, employee_id: e.target.value }))} data-testid="hol-emp" className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                <option value="">— pilih karyawan —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.nama}</option>)}
              </select>
            ) : <div />}
            {tanggalDipilih.length > 0 && (
              <div className="col-span-2 md:col-span-5 flex flex-wrap items-center gap-1.5 p-2 rounded-lg bg-emerald-50/60 border border-emerald-100"
                   data-testid="hol-tanggal-terpilih">
                <span className="text-xs text-emerald-800 font-medium">{tanggalDipilih.length} tanggal:</span>
                {tanggalDipilih.map(d => (
                  <span key={d} className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-emerald-800">
                    {d}
                    <button type="button" onClick={() => buangTanggal(d)} data-testid={`hol-buang-${d}`}
                      className="text-emerald-400 hover:text-rose-600 font-bold leading-none">×</button>
                  </span>
                ))}
                <button type="button" onClick={() => setTanggalDipilih([])}
                  className="ml-auto text-[11px] text-slate-500 hover:text-rose-600 underline">kosongkan</button>
              </div>
            )}
            <button onClick={add} data-testid="hol-add" className="h-10 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm inline-flex items-center justify-center gap-1 md:col-span-5"><Plus className="w-4 h-4" /> Simpan {tanggalDipilih.length > 1 ? `${tanggalDipilih.length} Hari Libur` : "Hari Libur"}</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="p-4 border-b border-slate-100 bg-emerald-50/60">
            <p className="text-sm font-semibold text-emerald-800">Libur Global ({globalItems.length})</p>
            <p className="text-xs text-slate-500">Berlaku untuk semua karyawan (mis. Tahun Baru, HUT RI)</p>
          </div>
          <ul className="divide-y divide-slate-100">
            {globalItems.map(h => (
              <li key={h.id} className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{h.name}</p>
                  <p className="text-xs font-mono text-slate-500">{h.date}</p>
                </div>
                {isAdmin && <button onClick={() => del(h.id)} className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>}
              </li>
            ))}
            {globalItems.length === 0 && <li className="p-6 text-center text-sm text-slate-500">Belum ada libur global untuk tahun {year}</li>}
          </ul>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="p-4 border-b border-slate-100 bg-violet-50/60">
            <p className="text-sm font-semibold text-violet-800">Libur Karyawan Individu ({empItems.length})</p>
            <p className="text-xs text-slate-500">Berlaku hanya untuk 1 karyawan (mis. izin khusus, day-off)</p>
          </div>
          <ul className="divide-y divide-slate-100">
            {empItems.map(h => (
              <li key={h.id} className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">{h.name}</p>
                  <p className="text-xs text-slate-500"><span className="font-mono">{h.date}</span> — <span className="text-violet-700">{empName(h.employee_id)}</span></p>
                </div>
                {isAdmin && <button onClick={() => del(h.id)} className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>}
              </li>
            ))}
            {empItems.length === 0 && <li className="p-6 text-center text-sm text-slate-500">Belum ada libur individu untuk tahun {year}</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ================= Lokasi Kantor / Geofence ================= */
function GeofenceTab({ isAdmin }) {
  const BRANCHES = useCabangOptions();
  const [items, setItems] = useState([]);
  // Backend (BranchLocationReq di hr_config.py) hanya menerima lat / lng / radius_meters.
  // Selama form ini memakai nama `radius_m`, angka yang diisi admin dibuang diam-diam dan
  // geofence terkunci di nilai default backend — karyawan bisa absen dari jarak yang salah.
  // Cabang awal KOSONG lalu diisi cabang pertama begitu daftar termuat (keputusan
  // owner 24 Sep 2026). Dulu "ZOA GROUP" — nama kantor pusat pemilik produk lama
  // yang tidak ada di daftar, jadi <select> tampak memilih opsi pertama padahal
  // lokasi tersimpan atas nama "ZOA GROUP".
  const [form, setForm] = useState({ branch: "", lat: "", lng: "", radius_meters: 50 });
  useEffect(() => {
    if (!form.branch && BRANCHES.length) setForm(s => (s.branch ? s : { ...s, branch: BRANCHES[0] }));
  }, [BRANCHES, form.branch]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/branch-locations").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const useCurrent = () => {
    if (!navigator.geolocation) { toast.error("Browser tidak mendukung geolocation"); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      pos => { setForm(s => ({ ...s, lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) })); setBusy(false); toast.success("Lokasi terkini diambil"); },
      err => { toast.error("Gagal ambil lokasi: " + err.message); setBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    if (!form.lat || !form.lng) { toast.error("Lat/Lng wajib"); return; }
    try {
      await api.put(`/branch-locations/${encodeURIComponent(form.branch)}`, {
        lat: parseFloat(form.lat), lng: parseFloat(form.lng),
        radius_meters: parseInt(form.radius_meters) || 50,
      });
      toast.success("Lokasi disimpan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (branch) => { if (!confirm(`Hapus lokasi ${branch}? Karyawan branch ini akan bebas absen (tanpa geofence).`)) return; await api.delete(`/branch-locations/${encodeURIComponent(branch)}`); load(); };
  // `radius_m` masih dibaca sebagai cadangan karena dokumen lama di MongoDB memakai nama itu
  // (attendance.py juga masih menoleransinya). Tanpa fallback, baris lama tampil radius kosong.
  const radiusDari = (it) => it.radius_meters ?? it.radius_m ?? 50;
  const edit = (it) => setForm({ branch: it.branch, lat: it.lat, lng: it.lng, radius_meters: radiusDari(it) });

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
        <b>Geofencing 50 m</b>: karyawan hanya bisa clock-in/out saat berada dalam radius (default 50 m) dari titik kantor yang di-set di sini. Cabang yang belum di-set = tidak ada batasan.
      </div>

      {isAdmin && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
          <p className="text-sm font-semibold text-slate-900 mb-3 inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-emerald-600" /> Set / Update Lokasi Kantor</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Branch</span>
              <select value={form.branch} onChange={e => setForm(s => ({ ...s, branch: e.target.value }))} data-testid="geo-branch"
                className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                {BRANCHES.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Latitude</span>
              <input value={form.lat} onChange={e => setForm(s => ({ ...s, lat: e.target.value }))} data-testid="geo-lat"
                placeholder="-6.200000" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Longitude</span>
              <input value={form.lng} onChange={e => setForm(s => ({ ...s, lng: e.target.value }))} data-testid="geo-lng"
                placeholder="106.816666" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Radius (meter)</span>
              <input type="number" value={form.radius_meters} onChange={e => setForm(s => ({ ...s, radius_meters: e.target.value }))} data-testid="geo-radius"
                className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
            </label>
            {/* Kolom "Alamat" dihapus: backend tidak punya field itu, jadi apa pun yang diketik
                admin hilang begitu disimpan. Menampilkan input yang isinya pasti dibuang lebih
                menyesatkan daripada tidak menyediakannya sama sekali. */}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <button onClick={useCurrent} disabled={busy} data-testid="geo-usecurrent"
              className="h-10 px-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium inline-flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />} Ambil Lokasi Saat Ini (HP/laptop)
            </button>
            {form.lat && form.lng && (
              <a href={`https://www.google.com/maps?q=${form.lat},${form.lng}`} target="_blank" rel="noreferrer"
                className="text-xs text-emerald-700 hover:text-emerald-800 underline">Lihat di Google Maps →</a>
            )}
            <button onClick={save} data-testid="geo-save"
              className="ml-auto h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium inline-flex items-center gap-1.5"><Save className="w-4 h-4" /> Simpan Lokasi</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            <th className="text-left py-3 px-4">Branch</th>
            <th className="text-left py-3 px-4">Koordinat</th>
            <th className="text-right py-3 px-4">Radius</th>
            <th className="text-right py-3 px-4"></th>
          </tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.branch} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4 font-medium text-slate-900 inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-emerald-600" /> {it.branch}</td>
                <td className="py-3 px-4 font-mono text-xs text-slate-600">{it.lat.toFixed(6)}, {it.lng.toFixed(6)}</td>
                <td className="py-3 px-4 text-right font-mono">{radiusDari(it)} m</td>
                <td className="py-3 px-4 text-right space-x-1">
                  <a href={`https://www.google.com/maps?q=${it.lat},${it.lng}`} target="_blank" rel="noreferrer" className="inline-block p-1.5 hover:bg-emerald-50 rounded text-emerald-600" title="Buka Google Maps"><MapPin className="w-4 h-4 inline" /></a>
                  {isAdmin && <button onClick={() => edit(it)} className="px-2 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50">Edit</button>}
                  {isAdmin && <button onClick={() => del(it.branch)} className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-sm text-slate-500">Belum ada lokasi. Tambahkan di form atas.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= Konfigurasi Payroll ================= */
function ConfigTab({ isSuper }) {
  const [config, setConfig] = useState(null);
  useEffect(() => { api.get("/config").then(r => setConfig(r.data)); }, []);
  const save = async () => {
    try {
      const body = { ...(config || {}) };
      delete body.key; delete body._id;
      await api.put("/config", body);
      toast.success("Konfigurasi disimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  if (!config) return <div className="p-6"><LoadingState loading jumlahKartu={1} data-testid="config-loading" /></div>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 max-w-2xl">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ["tunj_istri_pct", "Tunj. Istri/Suami (% Gapok)"],
          ["tunj_anak_pct", "Tunj. per Anak (% Gapok)"],
          ["max_anak", "Maks. Anak Ditanggung"],
          ["denda_telat_per_menit", "Denda Keterlambatan / menit (Rp)"],
          ["denda_alpha_pct", "Denda Alpha (% Gapok / hari)"],
          ["petty_min_balance_default", "Saldo minimal bawaan kas kecil (Rp)"],
          ["tarif_lembur_per_jam", "Tarif Lembur / jam (Rp)"],
          ["edit_window_minutes", "Durasi Edit Input Sales/Akuisisi (menit)"],
        ].map(([k, l]) => (
          <label key={k} className="block">
            <span className="text-xs font-medium text-slate-600">{l}</span>
            {(k === "denda_telat_per_menit" || k === "tarif_lembur_per_jam") ? (
              <InputRupiah value={config[k] ?? 0} disabled={!isSuper}
                onChange={n => setConfig(s => ({ ...s, [k]: n }))}
                testid={`cfg-${k}`}
                className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50" />
            ) : (
              <input type="number" value={config[k] ?? 0} disabled={!isSuper}
                onChange={e => setConfig(s => ({ ...s, [k]: parseFloat(e.target.value) || 0 }))}
                data-testid={`cfg-${k}`}
                className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50" />
            )}
          </label>
        ))}
      </div>

      <div className="mt-6 pt-5 border-t border-slate-100">
        <p className="text-sm font-semibold text-slate-900">Potongan Jam Kerja Kurang (Pulang Awal)</p>
        <p className="text-xs text-slate-500 mt-0.5 mb-4">
          Berlaku untuk semua karyawan. Bisa dikecualikan per orang lewat Data Karyawan &rsaquo; Informasi Kerja.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ["denda_pulang_awal_per_jam", "Denda Pulang Awal / jam (Rp)",
             "Dihitung proporsional per menit. Contoh: kurang 30 menit = setengah tarif."],
            ["pulang_awal_toleransi_menit", "Toleransi Pulang Awal (menit)",
             "Kekurangan di bawah angka ini tidak didenda. Isi 0 kalau tanpa toleransi."],
            ["min_jam_kerja_pct", "Ambang Kehadiran (% durasi shift)",
             "Jam kerja di bawah ini dianggap TIDAK HADIR (kena denda alpha, bukan denda pulang awal)."],
          ].map(([k, l, hint]) => (
            <label key={k} className="block">
              <span className="text-xs font-medium text-slate-600">{l}</span>
              {k === "denda_pulang_awal_per_jam" ? (
                <InputRupiah value={config[k] ?? 0} disabled={!isSuper}
                  onChange={n => setConfig(s => ({ ...s, [k]: n }))}
                  testid={`cfg-${k}`}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50" />
              ) : (
                <input type="number" value={config[k] ?? 0} disabled={!isSuper}
                  onChange={e => setConfig(s => ({ ...s, [k]: parseFloat(e.target.value) || 0 }))}
                  data-testid={`cfg-${k}`}
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm disabled:bg-slate-50" />
              )}
              <span className="block text-[10px] text-slate-400 mt-1 leading-snug">{hint}</span>
            </label>
          ))}
        </div>
      </div>
      {isSuper && (
        <div className="mt-6 flex justify-end">
          <button onClick={save} data-testid="btn-save-config" className="h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium inline-flex items-center gap-1.5"><Save className="w-4 h-4" /> Simpan Konfigurasi</button>
        </div>
      )}
    </div>
  );
}
